// RFC 4180-ish CSV utility. Small, deliberate, no edge-case-of-the-week
// features. Quotes fields when needed, doubles internal quotes, accepts
// both LF and CRLF on input, strips a leading UTF-8 BOM.

const CR = 0x0d;
const LF = 0x0a;
const QUOTE = 0x22;
const COMMA = 0x2c;

// Leading characters a spreadsheet treats as the start of a formula.
const FORMULA_LEAD = /^[=+\-@\t\r]/;
// The apostrophe escape written by escapeCell, undone by csvParse.
const ESCAPED_FORMULA_LEAD = /^'(?=[=+\-@\t\r])/;

export function csvStringify(rows) {
  return rows.map((row) => row.map(escapeCell).join(',')).join('\r\n') + '\r\n';
}

function escapeCell(v) {
  if (v == null) return '';
  let s = typeof v === 'string' ? v : String(v);
  if (s === '') return '';
  // Neutralize spreadsheet formula injection. Excel and Sheets evaluate a cell
  // whose first character is one of these, and quoting does not stop it — the
  // quotes are stripped during CSV parsing, then the formula runs. Vault values
  // can arrive from an untrusted 1PIF/CSV import, so an entry title like
  // =HYPERLINK(...) would execute when the user opens their own export.
  // csvParse strips this prefix back off, so export→import stays lossless
  // (phone numbers like "+1", which are the common benign case, survive).
  if (FORMULA_LEAD.test(s)) s = `'${s}`;
  // Quote when the field contains a comma, quote, CR, LF, or starts/ends
  // with whitespace (some tools mangle leading/trailing spaces).
  const needs = /[",\r\n]/.test(s) || /^\s|\s$/.test(s);
  if (!needs) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

/**
 * Parse a CSV string into an array of rows (each row is an array of strings).
 * Returns the rows; throws on structural errors.
 */
export function csvParse(text) {
  if (typeof text !== 'string') throw new Error('csvParse: expected string');
  // Strip UTF-8 BOM if present.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

  const rows = [];
  let row = [];
  let field = '';
  let i = 0;
  let inQuotes = false;
  const len = text.length;

  while (i < len) {
    const c = text.charCodeAt(i);

    if (inQuotes) {
      if (c === QUOTE) {
        // Look ahead for a doubled quote ("") which is an escaped quote.
        if (i + 1 < len && text.charCodeAt(i + 1) === QUOTE) {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += text[i];
      i++;
      continue;
    }

    if (c === QUOTE) {
      if (field === '') {
        inQuotes = true;
        i++;
        continue;
      }
      // A quote in the middle of an unquoted field — treat as a literal.
      field += '"';
      i++;
      continue;
    }
    if (c === COMMA) {
      row.push(field);
      field = '';
      i++;
      continue;
    }
    if (c === CR) {
      // Treat CR, CRLF, or LF as a row terminator.
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i++;
      if (i < len && text.charCodeAt(i) === LF) i++;
      continue;
    }
    if (c === LF) {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i++;
      continue;
    }
    field += text[i];
    i++;
  }

  if (inQuotes) throw new Error('csvParse: unterminated quoted field');
  // Flush the trailing field/row if the file didn't end with a newline.
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Undo escapeCell's anti-formula apostrophe so export→import is lossless.
  // Only strips an apostrophe that directly precedes a formula trigger, which
  // is the exact shape escapeCell writes.
  return rows.map((r) => r.map((cell) => cell.replace(ESCAPED_FORMULA_LEAD, '')));
}
